import axios from 'axios';
import { jwtDecode } from 'jwt-decode';
import { errorReporter } from '../services/errorReporter';
import { JOB_DRAFT_KEY_PREFIX } from '../constants';

export type UserTokenType = { sub: string; role: string; delegated: boolean };

export type UserAuthType = {
  username?: string;
  isAuthenticated: boolean;
  isEmployer: boolean;
  isStaff: boolean;
  isDelegated: boolean;
};

export const INITIAL_USER_AUTH: UserAuthType = {
  username: undefined,
  isAuthenticated: false,
  isEmployer: false,
  isStaff: false,
  isDelegated: false,
};

export function getUserAuth(token = getUserToken()): UserAuthType {
  if (!token) return INITIAL_USER_AUTH;
  const userData: UserTokenType = JSON.parse(token);
  return {
    username: userData.sub,
    isAuthenticated: !!userData,
    isEmployer: userData.role.toLowerCase() === 'employer',
    isStaff: userData.role.toLowerCase() === 'staff',
    isDelegated: userData.delegated,
  };
}

export type UserDataType = {
  userId: number;
  name: string;
  email: string;
};

function localStorageAvailable(object: any): object is Window {
  return 'localStorage' in object;
}

export const TOKEN_IDENTIFIER = 'dentalpost_auth_token';
export const TOKEN_USER_DATA = 'dentalpost_token_user_data';

type Listener = () => void;
const authChangeListeners = new Set<Listener>();

export function emit() {
  for (const listener of authChangeListeners) {
    listener();
  }
}

export function subscribeToAuthChanges(listener: Listener) {
  authChangeListeners.add(listener);
  if (typeof window === 'undefined')
    return () => authChangeListeners.delete(listener);

  const onStorage = (event: StorageEvent) => {
    if (
      event.storageArea === window.localStorage &&
      (event.key === TOKEN_USER_DATA || event.key === null)
    ) {
      emit();
    }
  };
  window.addEventListener('storage', onStorage);
  return () => {
    authChangeListeners.delete(listener);
    window.removeEventListener('storage', onStorage);
  };
}

const getAuthToken = () =>
  typeof window !== 'undefined' && localStorageAvailable(window)
    ? window.localStorage.getItem(TOKEN_IDENTIFIER)
    : null;

const getUserToken = () =>
  typeof window !== 'undefined' && localStorageAvailable(window)
    ? window.localStorage.getItem(TOKEN_USER_DATA)
    : null;

const setAuthToken = (token: string) => {
  try {
    if (token && typeof window !== 'undefined' && localStorageAvailable(window)) {
      const decodedToken = jwtDecode<UserTokenType>(token);

      axios.defaults.headers.common['Authorization'] = token;
      window.localStorage.setItem(TOKEN_IDENTIFIER, token);
      window.localStorage.setItem(
        TOKEN_USER_DATA,
        JSON.stringify(decodedToken)
      );
      // Tag subsequent error reports with the logged-in user. Single point of
      // capture — covers all login callsites (login page, registration, password
      // change, token refresh, sign-in-for-reports).
      errorReporter.setUserId(decodedToken.sub);
      emit(); //same-tab listeners
      return true;
    }
  } catch (err) {
    console.error('Could not save token data', err);
  }

  return false;
};

/**
 * Drop every in-progress job-posting draft. Drafts are already scoped per user,
 * but a torn-down session must not leave one employer's posting content sitting
 * in a shared browser for the next employer to sign in (DP-2290).
 */
function clearJobDrafts() {
  if (typeof window === 'undefined' || !localStorageAvailable(window)) return;
  try {
    // Collect first, then remove — removing while indexing by position skips
    // entries as the store shifts underneath the loop.
    const keys: string[] = [];
    for (let i = 0; i < window.localStorage.length; i++) {
      const key = window.localStorage.key(i);
      if (key?.startsWith(JOB_DRAFT_KEY_PREFIX)) keys.push(key);
    }
    keys.forEach((key) => window.localStorage.removeItem(key));
  } catch {
    // storage unavailable (private mode / quota) — sign-out must still proceed
  }
}

const destroyToken = () => {
  delete axios.defaults.headers.common['Authorization'];
  if (typeof window !== 'undefined' && localStorageAvailable(window)) {
    window.localStorage.removeItem(TOKEN_IDENTIFIER);
    window.localStorage.removeItem(TOKEN_USER_DATA);
  }
  clearJobDrafts();
  errorReporter.setUserId(undefined);
  emit(); //same-tab listeners
};

const userRole = () => {
  if (typeof window !== 'undefined' && localStorageAvailable(window)) {
    const tokenUserData = window.localStorage.getItem(TOKEN_USER_DATA);
    if (!!tokenUserData) {
      const userData = JSON.parse(tokenUserData);
      return userData.role || null;
    }
  } else {
    return null;
  }
};

const username = () => {
  const tokenUserData =
    typeof window !== 'undefined'
      ? window.localStorage.getItem(TOKEN_USER_DATA)
      : undefined;
  if (!!tokenUserData) {
    const userData = JSON.parse(tokenUserData);
    return userData.sub || null;
  }
};

const EMPLOYER_ROLE = 'employer';
const STAFF_ROLE = 'staff';
const isEmployerRole = () => userRole() === EMPLOYER_ROLE;
const isStaffRole = () => userRole() === STAFF_ROLE;

const encodedUsername = (anyUsername?: string) => {
  return encodeURIComponent(anyUsername || username());
};

const decodedUsername = (anyUsername?: string) => {
  return decodeURIComponent(anyUsername || username());
};

export {
  destroyToken,
  encodedUsername,
  decodedUsername,
  getAuthToken,
  isEmployerRole,
  isStaffRole,
  setAuthToken,
  getUserToken,
  userRole,
  username,
};
